import { FunctionComponent, useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Button, Header, Table, SpaceBetween, Modal, Toggle,
    Select, SelectProps, DateRangePicker, DateRangePickerProps,
    PropertyFilter, PropertyFilterProps,
    Pagination, CollectionPreferences,
} from '@amzn/awsui-components-react';
import { Order } from '@amzn/ito-client';
import { useFlashBarItemsActions } from 'common/UseFlashBarItems/useFlashBarItems';
import { AddBulkOrdersModal, AddBulkOrdersResult } from './AddBulkOrdersModal';
import { CompleteBulkOrderModal } from './CompleteBulkOrderModal';
import { RecordReceivedQuantityModal } from './RecordReceivedQuantityModal';
import { ReopenBulkOrderModal } from './ReopenBulkOrderModal';
import { getString, getPropertyFilterString } from 'common/ui-string-labels/ui-strings-utils';
import { buildDateRangeFilter, validateDateRange, DATE_RANGE_RELATIVE_OPTIONS, formatRelativeRange } from 'common/utils/date-range-filter-utils';
import { VENDOR_FILTER_OPTIONS, STATUS_FILTER_OPTIONS } from './interfaces';
import { BulkOrderStatus } from 'configuration/config';
import { buildColumnDefinitions, buildFilteringProperties, buildVisibleContentOptions, defaultPagingOptions, useSelectedOnlyView } from 'common/utils';
import { convertTokensToPropertyFilter } from 'common/utils/property-filter-helper';
import { describeError } from 'common/utils/error-utils';
import { ColumnSetting } from 'common/utils/config-models';
import { CurrentPage } from 'interfaces';
import { usePreferences } from 'hooks/usePreferences';
import { useReopenBulkOrder, useUpdateReceivedQuantity } from 'hooks/orders';
import { CSRFService } from 'services/csrf-service';
import { BulkOrdersService, BulkOrderPartialError } from 'services/bulk-orders-service';
import tableConfig from './bulk-orders.table-config.json';

const PREFERENCES_KEY = 'SubmittedBulkOrdersPreferences';
const columns: ColumnSetting[] = tableConfig.columns as ColumnSetting[];
const VISIBLE_CONTENT_COLUMN_OPTIONS = buildVisibleContentOptions(columns);
const FILTERING_PROPERTIES = buildFilteringProperties(columns);

export interface BaseListParams {
    vendor?: string;
    status?: string;
    dateFilter?: [string, string];
}

/** Builds the vendor/status/date portion of listBulkOrders params shared by both fetch paths. */
export function buildBaseListParams(
    vendorFilter: SelectProps.Option,
    statusFilter: SelectProps.Option,
    dateRange: DateRangePickerProps.Value | null,
): BaseListParams {
    const params: BaseListParams = {};
    if (vendorFilter.value && vendorFilter.value !== 'all') params.vendor = vendorFilter.value;
    if (statusFilter.value && statusFilter.value !== 'all') params.status = statusFilter.value;
    if (dateRange) {
        const filter = buildDateRangeFilter(dateRange);
        if (filter) params.dateFilter = filter;
    }
    return params;
}

export interface BulkOrdersTableProps {
    useFlashBarItemsActions: useFlashBarItemsActions;
}

export const BulkOrdersTable: FunctionComponent<BulkOrdersTableProps> = ({ useFlashBarItemsActions }) => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedItems, setSelectedItems] = useState<Order[]>([]);
    const [submitModalVisible, setSubmitModalVisible] = useState(false);
    const [completeQueue, setCompleteQueue] = useState<Order[] | null>(null);
    const [completeQueueTotal, setCompleteQueueTotal] = useState(0);
    const anyOrderCompletedRef = useRef(false);

    // Filters
    const [vendorFilter, setVendorFilter] = useState<SelectProps.Option>(VENDOR_FILTER_OPTIONS[0]);
    const [statusFilter, setStatusFilter] = useState<SelectProps.Option>(STATUS_FILTER_OPTIONS[0]);
    const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(null);
    const [filterQuery, setFilterQuery] = useState<PropertyFilterProps.Query>({ tokens: [], operation: 'and' });

    // Preferences (persisted to localStorage)
    const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
        preferencesKey: PREFERENCES_KEY,
        refreshData: () => {},
        columnsSettings: columns,
        defaultPaging: defaultPagingOptions[0].value,
    });

    const [currentPageIndex, setCurrentPageIndex] = useState<CurrentPage>({ index: 1, isPageReset: true });
    const [totalResults, setTotalResults] = useState(0);

    /** Fetches orders from the backend, matching ManageOrders' pattern: PropertyFilter
     * tokens are converted directly to a filters map (via convertTokensToPropertyFilter)
     * and sent in a single request. */
    const fetchOrders = useCallback(async (page: number) => {
        setLoading(true);
        try {
            const keyedFilters = convertTokensToPropertyFilter(filterQuery.tokens);
            const baseParams = buildBaseListParams(vendorFilter, statusFilter, dateRange);
            const pageSize = currentPreferences.pageSize!;

            const result = await BulkOrdersService.instance.listBulkOrders({
                ...baseParams,
                pageSize,
                pageIndex: page,
                filters: Object.keys(keyedFilters).length > 0 ? keyedFilters : undefined,
            });

            setOrders(result.orders);
            setTotalResults(result.total);
        } catch (error) {
            useFlashBarItemsActions.addErrorFlashBarItem(describeError(error, 'bulkOrders.flashbar.error'));
        } finally {
            setLoading(false);
        }
    }, [vendorFilter, statusFilter, dateRange, filterQuery, currentPreferences.pageSize]);

    const { showSelectedOnly, setShowSelectedOnly, displayedItems, pagesCount } = useSelectedOnlyView<Order>({
        selectedItems,
        pageSize: currentPreferences.pageSize!,
        currentPageIndex,
        setCurrentPageIndex,
        fallbackItems: orders,
        fallbackTotalResults: totalResults,
    });

    useEffect(() => {
        if (!showSelectedOnly) {
            fetchOrders(1);
        }
    }, [fetchOrders]);

    useEffect(() => {
        if (!currentPageIndex.isPageReset && !showSelectedOnly) {
            fetchOrders(currentPageIndex.index);
        }
    }, [currentPageIndex.index, currentPageIndex.isPageReset]);

    // Refetches page 1 on toggle-off (skipped by the effect above while showSelectedOnly is true) without clearing selection.
    const wasShowingSelectedOnly = useRef(false);
    useEffect(() => {
        if (wasShowingSelectedOnly.current && !showSelectedOnly) {
            fetchOrders(currentPageIndex.index);
        }
        wasShowingSelectedOnly.current = showSelectedOnly;
    }, [showSelectedOnly]);

    // Add Bulk Orders modal state
    const onSubmitBulkOrders = async (result: AddBulkOrdersResult) => {
        setSubmitModalVisible(false);
        useFlashBarItemsActions.addLoadingFlashBarItem?.(getString('bulkOrders.submittedOrders.submitting', { count: result.templates.length }));
        try {
            await BulkOrdersService.instance.submitBulkOrders(result);
            useFlashBarItemsActions.clearFlashBarItems?.();
            useFlashBarItemsActions.addSuccessFlashBarItem(getString('bulkOrders.submittedOrders.submitSuccess', { count: result.templates.length }));
            await fetchOrders(1);
        } catch (error) {
            useFlashBarItemsActions.clearFlashBarItems?.();
            if (error instanceof BulkOrderPartialError && error.createdOrders.length > 0) {
                useFlashBarItemsActions.addErrorFlashBarItem(
                    getString('bulkOrders.flashbar.error') + ` (${error.createdOrders.length}/${result.templates.length} succeeded)`
                );
            } else {
                useFlashBarItemsActions.addErrorFlashBarItem(describeError(error, 'bulkOrders.flashbar.error'));
            }
            await fetchOrders(1);
        }
    };

    const canComplete = selectedItems.length > 0 && selectedItems.every(o => o.status === BulkOrderStatus.PENDING_RESELLER);
    const canCancel = selectedItems.length > 0 && selectedItems.every(o => o.status === BulkOrderStatus.NEW || o.status === BulkOrderStatus.PENDING_RESELLER);
    const canReopen = selectedItems.length > 0 && selectedItems.every(o => o.status === BulkOrderStatus.COMPLETED);
    const canRecordReceivedQuantity = selectedItems.length > 0 && selectedItems.every(o => o.status !== BulkOrderStatus.CANCELLED && o.status !== BulkOrderStatus.COMPLETED);

    const confirmComplete = () => {
        anyOrderCompletedRef.current = false;
        setCompleteQueueTotal(selectedItems.length);
        setCompleteQueue(selectedItems);
    };

    const dismissCompleteModal = (shouldRefreshTable: boolean = false, failed: boolean = false, abortQueue: boolean = false) => {
        const isMultiOrderFlow = completeQueueTotal > 1;
        const remaining = completeQueue!.slice(1);
        if (shouldRefreshTable) {
            anyOrderCompletedRef.current = true;
        }

        const finishQueue = () => {
            setCompleteQueue(null);
            if (anyOrderCompletedRef.current) {
                setSelectedItems([]);
                fetchOrders(1);
            }
        };

        if (abortQueue) {
            finishQueue();
            return;
        }

        if (!shouldRefreshTable && !failed && isMultiOrderFlow && remaining.length > 0) {
            const skippedOrder = completeQueue![0];
            useFlashBarItemsActions.addInfoFlashBarItem?.(
                getString('bulkOrders.submittedOrders.orderSkipped', {
                    orderReference: skippedOrder.orderReference || skippedOrder.orderId,
                })
            );
        }

        if (remaining.length === 0) {
            finishQueue();
            return;
        }
        setCompleteQueue(remaining);
    };

    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    // Non-null while the reopen flow is active; the head is the order shown in the modal.
    const [reopenQueue, setReopenQueue] = useState<Order[] | null>(null);
    const [reopenQueueTotal, setReopenQueueTotal] = useState(0);
    const anyReopenedRef = useRef(false);
    const { reopenResponse, isProcessing: isReopening, error: reopenError, doReopenBulkOrder } = useReopenBulkOrder();
    // Non-null while the received-quantity flow is active; the head is the order shown in the modal.
    const [receivedQuantityQueue, setReceivedQuantityQueue] = useState<Order[] | null>(null);
    const [receivedQuantityQueueTotal, setReceivedQuantityQueueTotal] = useState(0);
    const anyReceivedQuantitySavedRef = useRef(false);
    const { updateResponse, isProcessing: isUpdatingReceivedQuantity, error: receivedQuantityError, doUpdateReceivedQuantity } = useUpdateReceivedQuantity();

    const advanceReceivedQuantityQueue = () => {
        setReceivedQuantityQueue(prev => {
            const remaining = prev!.slice(1);
            return remaining.length === 0 ? null : remaining;
        });
    };

    useEffect(() => {
        if (updateResponse) {
            anyReceivedQuantitySavedRef.current = true;
            const messageKey = updateResponse.status === BulkOrderStatus.COMPLETED
                ? 'bulkOrders.submittedOrders.receivedQuantityAutoCompleted'
                : 'bulkOrders.submittedOrders.receivedQuantitySuccess';
            useFlashBarItemsActions.addSuccessFlashBarItem(getString(messageKey));
            advanceReceivedQuantityQueue();
        }
    }, [updateResponse]);

    // Independent of updateResponse: a failed request never sets it, so error handling must not be gated on it.
    useEffect(() => {
        if (receivedQuantityError?.hasError) {
            useFlashBarItemsActions.addErrorFlashBarItem(describeError({ message: receivedQuantityError.message }, 'bulkOrders.flashbar.error'));
        }
    }, [receivedQuantityError?.hasError]);

    // The queue just drained (transitioned from non-empty to null); refresh the table and clear selection.
    useEffect(() => {
        if (receivedQuantityQueue === null && anyReceivedQuantitySavedRef.current) {
            setSelectedItems([]);
            fetchOrders(1);
            anyReceivedQuantitySavedRef.current = false;
        }
    }, [receivedQuantityQueue]);

    const onOpenRecordReceivedQuantity = () => {
        anyReceivedQuantitySavedRef.current = false;
        setReceivedQuantityQueueTotal(selectedItems.length);
        setReceivedQuantityQueue(selectedItems);
    };

    const onSkipReceivedQuantity = () => {
        advanceReceivedQuantityQueue();
    };

    const onAbortReceivedQuantityQueue = () => {
        const hadSavedOrders = anyReceivedQuantitySavedRef.current;
        anyReceivedQuantitySavedRef.current = false;
        setReceivedQuantityQueue(null);
        if (hadSavedOrders) {
            fetchOrders(1);
        }
    };

    const onRecordReceivedQuantity = async (receivedQuantity: number) => {
        const order = receivedQuantityQueue![0];
        const orderedQuantity = order.items?.[0]?.quantity;
        const shouldAutoComplete = order.status !== BulkOrderStatus.COMPLETED && orderedQuantity !== undefined && receivedQuantity >= orderedQuantity;
        try {
            const csrfService = new CSRFService();
            const csrfToken = await csrfService.getToken();
            doUpdateReceivedQuantity({
                order,
                receivedQuantity,
                csrfToken,
                ...(shouldAutoComplete ? { status: BulkOrderStatus.COMPLETED } : {}),
            });
        } catch (error) {
            useFlashBarItemsActions.addErrorFlashBarItem(describeError(error, 'bulkOrders.flashbar.error'));
        }
    };

    const advanceReopenQueue = () => {
        setReopenQueue(prev => {
            const remaining = prev!.slice(1);
            return remaining.length === 0 ? null : remaining;
        });
    };

    useEffect(() => {
        if (reopenResponse) {
            anyReopenedRef.current = true;
            useFlashBarItemsActions.addSuccessFlashBarItem(getString('bulkOrders.submittedOrders.reopenSuccess'));
            advanceReopenQueue();
        }
    }, [reopenResponse]);

    // Independent of reopenResponse: a failed request never sets it, so error handling must not be gated on it.
    useEffect(() => {
        if (reopenError?.hasError) {
            useFlashBarItemsActions.addErrorFlashBarItem(describeError({ message: reopenError.message }, 'bulkOrders.flashbar.error'));
        }
    }, [reopenError?.hasError]);

    // The queue just drained (transitioned from non-empty to null); refresh the table and clear selection.
    useEffect(() => {
        if (reopenQueue === null && anyReopenedRef.current) {
            setSelectedItems([]);
            fetchOrders(1);
            anyReopenedRef.current = false;
        }
    }, [reopenQueue]);

    const onOpenReopen = () => {
        anyReopenedRef.current = false;
        setReopenQueueTotal(selectedItems.length);
        setReopenQueue(selectedItems);
    };

    const onSkipReopen = () => {
        advanceReopenQueue();
    };

    const onAbortReopenQueue = () => {
        const hadReopenedOrders = anyReopenedRef.current;
        anyReopenedRef.current = false;
        setReopenQueue(null);
        if (hadReopenedOrders) {
            fetchOrders(1);
        }
    };

    const onReopen = async (reason: string) => {
        const order = reopenQueue![0];
        try {
            const csrfService = new CSRFService();
            const csrfToken = await csrfService.getToken();
            doReopenBulkOrder({ order, reason, csrfToken });
        } catch (error) {
            useFlashBarItemsActions.addErrorFlashBarItem(describeError(error, 'bulkOrders.flashbar.error'));
        }
    };

    const onCancel = async () => {
        setCancelModalVisible(false);
        useFlashBarItemsActions.addLoadingFlashBarItem?.(getString('bulkOrders.submittedOrders.cancelling', { count: selectedItems.length }));
        const results = await Promise.allSettled(
            selectedItems.map(item =>
                BulkOrdersService.instance.updateOrderStatus(item.orderId!, BulkOrderStatus.CANCELLED)
            )
        );
        const failedCount = results.filter(r => r.status === 'rejected').length;
        useFlashBarItemsActions.clearFlashBarItems?.();
        if (failedCount === 0) {
            useFlashBarItemsActions.addSuccessFlashBarItem(
                getString('bulkOrders.submittedOrders.cancelSuccess', { count: selectedItems.length })
            );
        } else {
            const firstRejection = results.find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
            useFlashBarItemsActions.addErrorFlashBarItem(
                describeError(firstRejection?.reason, 'bulkOrders.flashbar.error')
            );
        }
        setSelectedItems([]);
        await fetchOrders(1);
    };

    return (
        <div style={{ padding: '0 24px' }}>
            <Table<Order>
                data-testid="bulk-orders-table"
                variant="full-page"
                selectionType="multi"
                trackBy="orderId"
                resizableColumns
                wrapLines
                selectedItems={selectedItems}
                onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
                header={
                    <Header
                        variant="h1"
                        counter={`(${totalResults})`}
                        description={getString('bulkOrders.submittedOrders.description')}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button data-testid="bulk-orders-complete-button" onClick={confirmComplete} disabled={!canComplete}>{getString('bulkOrders.submittedOrders.completeOrder')}</Button>
                                <Button data-testid="bulk-orders-cancel-button" onClick={() => setCancelModalVisible(true)} disabled={!canCancel}>{getString('bulkOrders.submittedOrders.cancelOrder')}</Button>
                                <Button data-testid="bulk-orders-reopen-button" onClick={onOpenReopen} disabled={!canReopen}>{getString('bulkOrders.submittedOrders.reopenOrder')}</Button>
                                <Button data-testid="bulk-orders-received-quantity-button" onClick={onOpenRecordReceivedQuantity} disabled={!canRecordReceivedQuantity}>{getString('bulkOrders.submittedOrders.recordReceivedQuantity')}</Button>
                                <Button data-testid="bulk-orders-add-button" variant="primary" onClick={() => setSubmitModalVisible(true)}>{getString('bulkOrders.submittedOrders.addBulkOrders')}</Button>
                            </SpaceBetween>
                        }
                    >
                        {getString('bulkOrders.submittedOrders.title')}
                    </Header>
                }
                filter={
                    <SpaceBetween size="s">
                        {selectedItems.length > 0 && (
                            <Box data-testid="bulk-orders-selection-count-badge">
                                <SpaceBetween direction="horizontal" size="s">
                                    <span>{getString('bulkOrders.submittedOrders.ordersSelected', { count: String(selectedItems.length) })}</span>
                                    <Toggle
                                        data-testid="bulk-orders-show-selected-only-toggle"
                                        checked={showSelectedOnly}
                                        onChange={({ detail }) => setShowSelectedOnly(detail.checked)}
                                    >
                                        {getString('bulkOrders.submittedOrders.showSelectedOnlyToggle')}
                                    </Toggle>
                                </SpaceBetween>
                            </Box>
                        )}
                        <SpaceBetween direction="horizontal" size="s">
                            <div data-testid="bulk-orders-property-filter" style={{ width: "500px" }}>
                                <PropertyFilter
                                    query={filterQuery}
                                    onChange={({ detail }) => setFilterQuery(detail)}
                                    filteringProperties={FILTERING_PROPERTIES}
                                    filteringPlaceholder={getString('bulkOrders.table.filterPlaceholder')}
                                    i18nStrings={getPropertyFilterString()}
                                    disabled={showSelectedOnly}
                                    expandToViewport
                                />
                            </div>
                            <Select
                                selectedOption={vendorFilter}
                                onChange={({ detail }) => setVendorFilter(detail.selectedOption)}
                                options={VENDOR_FILTER_OPTIONS}
                                selectedAriaLabel="Selected"
                                disabled={showSelectedOnly}
                            />
                            <Select
                                selectedOption={statusFilter}
                                onChange={({ detail }) => setStatusFilter(detail.selectedOption)}
                                options={STATUS_FILTER_OPTIONS}
                                selectedAriaLabel="Selected"
                                disabled={showSelectedOnly}
                            />
                            <DateRangePicker
                                value={dateRange}
                                onChange={({ detail }) => setDateRange(detail.value)}
                                placeholder={getString('bulkOrders.filters.dateRangePlaceholder')}
                                relativeOptions={DATE_RANGE_RELATIVE_OPTIONS}
                                isValidRange={validateDateRange}
                                disabled={showSelectedOnly}
                                i18nStrings={{
                                    relativeModeTitle: getString('manageOrders.dateRangePicker.relativeModeTitle'),
                                    absoluteModeTitle: getString('manageOrders.dateRangePicker.absoluteModeTitle'),
                                    relativeRangeSelectionHeading: getString('manageOrders.dateRangePicker.relativeRangeSelectionHeading'),
                                    cancelButtonLabel: getString('manageOrders.dateRangePicker.cancelButtonLabel'),
                                    applyButtonLabel: getString('manageOrders.dateRangePicker.applyButtonLabel'),
                                    clearButtonLabel: getString('manageOrders.dateRangePicker.clearButtonLabel'),
                                    customRelativeRangeOptionLabel: getString('manageOrders.dateRangePicker.customRelativeRangeOptionLabel'),
                                    customRelativeRangeOptionDescription: getString('manageOrders.dateRangePicker.customRelativeRangeOptionDescription'),
                                    customRelativeRangeDurationLabel: getString('manageOrders.dateRangePicker.customRelativeRangeDurationLabel'),
                                    customRelativeRangeDurationPlaceholder: getString('manageOrders.dateRangePicker.customRelativeRangeDurationPlaceholder'),
                                    customRelativeRangeUnitLabel: getString('manageOrders.dateRangePicker.customRelativeRangeUnitLabel'),
                                    formatRelativeRange,
                                }}
                                dateOnly
                            />
                        </SpaceBetween>
                        {showSelectedOnly && (
                            <Box color="text-status-info" fontSize="body-s">
                                {getString('bulkOrders.submittedOrders.showSelectedOnlyIndicator')}
                            </Box>
                        )}
                    </SpaceBetween>
                }
                columnDefinitions={buildColumnDefinitions(tableConfig.columns as ColumnSetting[], currentPreferences.visibleContent)}
                items={displayedItems ?? []}
                loading={loading}
                loadingText={getString('bulkOrders.table.loadingText')}
                stripedRows={currentPreferences.stripedRows}
                pagination={
                    <Pagination
                        currentPageIndex={Math.min(currentPageIndex.index, pagesCount) || 1}
                        pagesCount={pagesCount}
                        onChange={({ detail }) => setCurrentPageIndex({ index: detail.currentPageIndex, isPageReset: false })}
                    />
                }
                preferences={
                    <CollectionPreferences
                        title={getString('common.preferences.title')}
                        confirmLabel={getString('common.preferences.confirmLabel')}
                        cancelLabel={getString('common.preferences.cancelLabel')}
                        preferences={currentPreferences}
                        onConfirm={onPreferencesConfirmHandler}
                        pageSizePreference={{ title: getString('common.preferences.pageSize.title'), options: defaultPagingOptions }}
                        stripedRowsPreference={{ label: getString('common.preferences.stripedRows.label'), description: getString('common.preferences.stripedRows.description') }}
                        visibleContentPreference={{ title: getString('common.preferences.visibleContentPreference.title'), options: [{ label: getString('common.preferences.visibleContentPreference.columns'), options: VISIBLE_CONTENT_COLUMN_OPTIONS }] }}
                    />
                }
                empty={<Box textAlign="center"><b>{getString('bulkOrders.table.emptyTitle')}</b></Box>}
            />

            {completeQueue && completeQueue.length > 0 && (
                <CompleteBulkOrderModal
                    key={completeQueue[0].orderId}
                    showModal={true}
                    onDismiss={dismissCompleteModal}
                    flashBarItemsActions={useFlashBarItemsActions}
                    selectedItem={completeQueue[0]}
                    progress={
                        completeQueueTotal > 1
                            ? { current: completeQueueTotal - completeQueue.length + 1, total: completeQueueTotal }
                            : undefined
                    }
                />
            )}

            <AddBulkOrdersModal
                visible={submitModalVisible}
                onDismiss={() => setSubmitModalVisible(false)}
                onSubmit={onSubmitBulkOrders}
            />

            <Modal
                data-testid="cancel-bulk-order-modal"
                visible={cancelModalVisible}
                onDismiss={() => setCancelModalVisible(false)}
                header={getString('bulkOrders.submittedOrders.cancelOrder')}
                footer={
                    <Box float="right"><SpaceBetween direction="horizontal" size="xs">
                        <Button data-testid="cancel-bulk-order-back" variant="link" onClick={() => setCancelModalVisible(false)}>{getString('common.preferences.cancelLabel')}</Button>
                        <Button data-testid="cancel-bulk-order-confirm" variant="primary" onClick={onCancel}>{getString('bulkOrders.submittedOrders.cancelOrder')}</Button>
                    </SpaceBetween></Box>
                }
            >
                {getString('bulkOrders.submittedOrders.cancelConfirmMessage', { count: selectedItems.length })}
            </Modal>

            {reopenQueue && reopenQueue.length > 0 && (
                <ReopenBulkOrderModal
                    key={reopenQueue[0].orderId}
                    visible={true}
                    order={reopenQueue[0]}
                    isProcessing={isReopening}
                    progress={
                        reopenQueueTotal > 1
                            ? { current: reopenQueueTotal - reopenQueue.length + 1, total: reopenQueueTotal }
                            : undefined
                    }
                    onDismiss={onAbortReopenQueue}
                    onSkip={reopenQueueTotal > 1 ? onSkipReopen : undefined}
                    onConfirm={onReopen}
                />
            )}

            {receivedQuantityQueue && receivedQuantityQueue.length > 0 && (
                <RecordReceivedQuantityModal
                    key={receivedQuantityQueue[0].orderId}
                    visible={true}
                    order={receivedQuantityQueue[0]}
                    isProcessing={isUpdatingReceivedQuantity}
                    progress={
                        receivedQuantityQueueTotal > 1
                            ? { current: receivedQuantityQueueTotal - receivedQuantityQueue.length + 1, total: receivedQuantityQueueTotal }
                            : undefined
                    }
                    onDismiss={onAbortReceivedQuantityQueue}
                    onSkip={receivedQuantityQueueTotal > 1 ? onSkipReceivedQuantity : undefined}
                    onConfirm={onRecordReceivedQuantity}
                />
            )}
        </div>
    );
};